import { useEffect, useState } from 'react'

export interface HeartbeatAgo {
  /** Whole seconds since the last heartbeat, or null if none received. */
  seconds: number | null
  /** True when the gap exceeds the stale threshold (heartbeats ~ every 30s). */
  isStale: boolean
}

// Server heartbeats arrive roughly every 30s; flag anything beyond ~2.5x that
// as stale so a missed beat or two shows up as a warning in the panel.
const STALE_AFTER_MS = 75_000

/**
 * Ticks once per second to report how long ago the last WebSocket heartbeat
 * arrived. Kept separate from the (frozen) diagnostics snapshot so only this
 * small value re-renders on the interval.
 */
export function useHeartbeatAgo(lastPingAt: number | null): HeartbeatAgo {
  const [now, setNow] = useState(() => Date.now())

  // Start the 1s tick once a heartbeat has been seen, and keep it running.
  // Keyed on `hasReceived` (not `lastPingAt`) so a new heartbeat every ~30s
  // doesn't tear down and recreate the interval — the render already computes
  // `now - lastPingAt` with both current, so the interval only needs to exist
  // once. After a disconnect lastPingAt is kept (not nulled), so the gap keeps
  // growing past the stale threshold — surfacing "Ns ago (stale)".
  const hasReceived = lastPingAt !== null
  useEffect(() => {
    if (!hasReceived) return
    // Resync immediately: when the first heartbeat arrives, `now` is still the
    // (possibly stale) mount value, which would show an inflated "Ns ago" until
    // the first interval tick a second later.
    setNow(Date.now())
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [hasReceived])

  if (lastPingAt === null) {
    return { seconds: null, isStale: false }
  }

  const elapsed = Math.max(0, now - lastPingAt)
  return {
    seconds: Math.floor(elapsed / 1000),
    isStale: elapsed > STALE_AFTER_MS,
  }
}
